import React, { useState, useEffect, useRef } from 'react';
import { ProgressBar } from '../components/processing/ProgressBar';
import { MainLayout } from '../layouts/MainLayout';
import type { ModelStatus } from '../components/processing/ModelRow';
import { getModelLogo, ModelLogo } from '../utils/logoMapping';
import { getModelDisplayInfo } from '../utils/modelDisplayName';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { useIsTruncated } from '../hooks/useIsTruncated';
import { useAuth } from '../contexts';
import { AnonymousBanner } from '../components/shared';

// Mock data types (will be replaced with real store selectors)
interface ModelInfo {
  id: string;
  name: string;
  logo: string;
  status: ModelStatus;
}

interface ProcessingPageNewProps {
  comparisonId?: string;
  title?: string;
  prompt?: string;
  modelsUsed?: string[];
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  completedModels?: number;
  totalModels?: number;
}

// Mock data for insights
const mockRunKey = "run_20250925_001";

// Logging helper (will be enhanced in Step 11)
const logUI = (event: { op: string; component: string; success: boolean; [key: string]: any }) => {
  console.log('[UI Event]', JSON.stringify({
    timestamp: new Date().toISOString(),
    correlationId: mockRunKey,
    runKey: mockRunKey,
    ...event
  }));
};

export const ProcessingPageNew: React.FC<ProcessingPageNewProps> = ({
  comparisonId,
  title,
  prompt,
  modelsUsed,
  status: apiStatus,
  completedModels = 0,
  totalModels = 0
}) => {
  // Step 1: Basic state
  const [isProcessing, setIsProcessing] = useState(true);
  const runStatus = apiStatus || 'processing'; // Use API status instead of local state
  const displayStatus = runStatus === 'pending' ? 'processing' : runStatus; // Normalize pending to show spinner immediately
  const paywallEnabled = useFeatureFlag('PAYWALL');
  // NEW_DESIGN_BETA graduated - features always enabled
  const { user } = useAuth();
  const isAnonymous = !user;

  // Step 2: Prompt card state
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  // Ref for prompt text element to measure if truncated
  const promptTextRef = useRef<HTMLParagraphElement>(null);
  const isTruncated = useIsTruncated(title, 'line-clamp-3', promptTextRef);

  // Step 3: Progress state - Build from API data
  const [modelStatuses, setModelStatuses] = useState<ModelInfo[]>(() => {
    if (modelsUsed && modelsUsed.length > 0) {
      return modelsUsed.map((modelId: string) => {
        const displayInfo = getModelDisplayInfo(modelId);
        const logo = getModelLogo(modelId);
        return {
          id: modelId,
          name: displayInfo.displayName,
          logo: logo || '',
          status: 'queued' as ModelStatus
        };
      });
    }
    // Fallback to empty array
    return [];
  });
  // Calculate progress from real API data
  const progress = totalModels > 0 ? (completedModels / totalModels) * 100 : 0;

  // Step 4: Prediction game state
  const [selectedPrediction, setSelectedPrediction] = useState<string | null>(null);
  const predictionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Step 7: Timer state
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Step 8: Confetti state
  const [showConfetti, setShowConfetti] = useState(false);
  const [winnerId] = useState<string>('gpt-4'); // Mock winner

  useEffect(() => {
    logUI({ op: 'ui_mount', component: 'ProcessingPageNew', success: true });
  }, []);

  // Step 7: Timer effect
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isProcessing]);

  // Update model statuses based on completion progress
  useEffect(() => {
    if (!modelsUsed || modelsUsed.length === 0) return;

    setModelStatuses(prev => {
      return prev.map((model, index) => {
        // Mark models as done based on completed count
        if (index < completedModels) {
          return { ...model, status: 'done' as ModelStatus };
        } else if (index === completedModels && displayStatus === 'processing') {
          return { ...model, status: 'working' as ModelStatus };
        } else {
          return { ...model, status: 'queued' as ModelStatus };
        }
      });
    });
  }, [completedModels, modelsUsed, apiStatus, displayStatus]);

  // Step 8: Confetti effect on completion
  useEffect(() => {
    if (runStatus === 'completed' && selectedPrediction === winnerId) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReducedMotion) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 1400);
        logUI({ op: 'predict_result', component: 'ProcessingPageNew', success: true, correct: true });
      }
    } else if (runStatus === 'completed' && selectedPrediction && selectedPrediction !== winnerId) {
      logUI({ op: 'predict_result', component: 'ProcessingPageNew', success: true, correct: false });
    }
  }, [runStatus, selectedPrediction, winnerId]);

  // Step 2: Copy prompt handler - copies the comparison title shown in the UI
  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(title || '');
      logUI({ op: 'prompt_copy', component: 'ProcessingPageNew/PromptCard', success: true });
      // Could add toast notification here
    } catch (err) {
      logUI({ op: 'prompt_copy', component: 'ProcessingPageNew/PromptCard', success: false, err_code: 'clipboard_failed' });
    }
  };

  // Step 2: Toggle expand handler
  const handleToggleExpand = () => {
    setIsPromptExpanded(!isPromptExpanded);
    logUI({ op: 'prompt_expand_toggle', component: 'ProcessingPageNew/PromptCard', success: true, expanded: !isPromptExpanded });
  };

  // Step 4: Prediction selection handler
  const handlePredictionSelect = (modelId: string, index: number) => {
    setSelectedPrediction(modelId);
    logUI({ op: 'predict_select', component: 'ProcessingPageNew/PredictionChallenge', success: true, modelId });
  };

  // Step 4: Keyboard navigation for prediction tiles
  const handlePredictionKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (index + 1) % modelStatuses.length;
      predictionRefs.current[nextIndex]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (index - 1 + modelStatuses.length) % modelStatuses.length;
      predictionRefs.current[prevIndex]?.focus();
    }
  };

  // Step 6: Insights navigation
  // Removed: Site Insights module is suppressed.

  // Step 7: Cancel handler with API delete
  const handleCancelConfirm = async () => {
    setShowCancelModal(false);
    setIsProcessing(false);

    try {
      // Delete comparison from database
      const response = await fetch('/api/comparison/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comparisonId }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete comparison');
      }

      logUI({ op: 'run_cancel_success', component: 'ProcessingPageNew/ActionsBar', success: true });

      // Navigate back to /compare page
      window.location.href = '/compare';
    } catch (error) {
      console.error('Cancel error:', error);
      logUI({ op: 'run_cancel_error', component: 'ProcessingPageNew/ActionsBar', success: false });
      // Still navigate away even if delete fails
      window.location.href = '/compare';
    }
  };

  // Step 7: Edit & Resubmit handler
  const handleEditResubmit = () => {
    setShowEditModal(true);
    logUI({ op: 'run_edit_attempt', component: 'ProcessingPageNew/ActionsBar', success: true });
  };

  const persistEditState = () => {
    const promptToStore = title || prompt;
    if (promptToStore) sessionStorage.setItem('editPrompt', promptToStore);
    if (modelsUsed && modelsUsed.length > 0) {
      sessionStorage.setItem('editModels', JSON.stringify(modelsUsed));
    }
  };

  // Step 7: Edit & Resubmit confirm handler with API delete
  const handleEditConfirm = async () => {
    setShowEditModal(false);
    setIsProcessing(false);
    persistEditState();

    try {
      if (!comparisonId) {
        throw new Error('Missing comparisonId for delete');
      }

      // Delete current comparison from database
      const response = await fetch('/api/comparison/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comparisonId }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete comparison');
      }

      logUI({ op: 'run_edit_success', component: 'ProcessingPageNew/ActionsBar', success: true });
    } catch (error) {
      console.error('Edit error:', error);
      logUI({ op: 'run_edit_error', component: 'ProcessingPageNew/ActionsBar', success: false, err_code: (error as Error).message });
    }

    // Navigate to /compare page even if delete fails, keeping stored prompt/models for prefill
    window.location.href = '/compare';
  };

  // Step 7: Timer formatter
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const predictionLayout = {
    container: 'grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3',
    card: 'p-3 sm:p-4 rounded-lg border-2 transition-all min-h-[110px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900',
    logo: 'w-12 h-12 sm:w-14 sm:h-14',
    logoWrapper: 'w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-2 flex items-center justify-center',
    title: 'text-sm sm:text-base font-semibold text-white',
    selected: 'mt-1 text-cyan-300 text-xs sm:text-sm'
  };

  // Step 3: Status color helper
  const getStatusColor = (status: ModelStatus): string => {
    switch (status) {
      case 'done': return 'bg-green-500/20 text-green-300 border border-green-400/30';
      case 'working': return 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/30';
      case 'error': return 'bg-red-500/20 text-red-300 border border-red-400/30';
      case 'stopped': return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      case 'queued':
      default: return 'bg-slate-600/20 text-slate-400 border border-slate-500/30';
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-900">
        {/* Step 8: Confetti overlay */}
      {showConfetti && (
        <div
          data-testid="confetti"
          className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center"
          role="status"
          aria-live="polite"
          aria-label="Congratulations! You predicted correctly!"
        >
          <div className="text-6xl animate-bounce">🎉</div>
          {/* Simple confetti effect - could be enhanced with a library */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-gradient-to-br from-cyan-400 to-purple-400 rounded-full animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 500}ms`,
                  animationDuration: `${1000 + Math.random() * 500}ms`
                }}
              />
            ))}
          </div>
        </div>
      )}

      <main role="main" className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32 md:pb-8">
        {/* Anonymous User Banner */}
        {isAnonymous && (
          <AnonymousBanner
            className="mb-6"
          />
        )}

        {/* Step 1: Header */}
        <div className="mb-6 sm:mb-8">
          <h1
            className="text-2xl sm:text-3xl font-bold text-white outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 rounded"
          >
            Processing Your Prompt
          </h1>
        </div>

        {/* Step 2: Prompt Card */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 border-l-4 border-l-cyan-500 rounded-xl p-4 sm:p-6 relative">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 pr-12 sm:pr-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">Your Prompt</h2>
              <button
                onClick={handleCopyPrompt}
                className="p-2 min-h-[44px] rounded-lg bg-slate-800/70 border border-slate-700/70 text-slate-300 hover:text-white hover:bg-slate-700/70 transition-colors absolute right-4 top-4 sm:static sm:right-auto sm:top-auto flex items-center justify-center"
                title="Copy full prompt to clipboard"
                aria-label="Copy full prompt to clipboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span className="sr-only">Copy full prompt to clipboard</span>
              </button>
            </div>
            <p
              ref={promptTextRef}
              className={`text-sm sm:text-base text-slate-300 ${!isPromptExpanded ? 'line-clamp-3' : ''}`}
            >
              {title || 'No prompt provided'}
            </p>
            {isTruncated && (
              <button
                onClick={handleToggleExpand}
                className="mt-2 text-sm text-cyan-300 hover:text-cyan-200 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-1"
                aria-expanded={isPromptExpanded}
                aria-label={isPromptExpanded ? 'Show less of the prompt' : 'Show more of the prompt'}
              >
                {isPromptExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>

        {/* Step 2.5: Timer & Actions Bar */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-slate-300 text-sm flex items-center gap-2">
                <span className="text-lg">⏱️</span>
                <span className="font-mono text-base">{formatTime(elapsedSeconds)}</span>
                <span className="text-slate-400">elapsed</span>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setShowCancelModal(true);
                    logUI({ op: 'run_cancel_attempt', component: 'ProcessingPageNew/ActionsBar', success: true });
                  }}
                  disabled={!isProcessing}
                  className="flex-1 sm:flex-none px-4 py-2 bg-red-500/20 text-red-300 border border-red-400/30 rounded-md hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-red-400 focus:outline-none min-h-[44px]"
                >
                  Cancel Prompt
                </button>
                <button
                  onClick={handleEditResubmit}
                  className="flex-1 sm:flex-none px-4 py-2 bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 rounded-md hover:bg-cyan-500/30 transition-colors focus:ring-2 focus:ring-cyan-400 focus:outline-none min-h-[44px]"
                >
                  Edit & Resubmit
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Overall Progress */}
        <div className="mb-6 sm:mb-8">
          <ProgressBar
            progress={progress}
            status={displayStatus}
            totalModels={totalModels}
          />

          {/* Per-model progress */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {modelStatuses.map((model) => (
              <div
                key={model.id}
                className="flex min-w-0 items-center gap-3 bg-slate-800/20 backdrop-blur-sm border border-slate-700/30 rounded-lg p-3"
              >
                <span className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <ModelLogo modelId={model.id} modelName={model.name} size="md" />
                </span>
                <span className="text-sm text-slate-300 flex-1">{model.name}</span>
                <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${getStatusColor(model.status)}`}>
                  {model.status === 'queued' ? 'Queued' : model.status === 'working' ? 'Working' : model.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step 4: Prediction Challenge - Only show with multiple models */}
        {modelsUsed && modelsUsed.length > 1 && (
          <div className="mb-6 sm:mb-8">
            <div className="bg-gradient-to-br from-purple-900/30 to-cyan-900/30 border border-purple-500/30 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">🎯 Prediction Challenge</h2>
                  <p className="text-sm sm:text-base text-slate-300 mb-3">Which AI will give the best response?</p>
                </div>
                <div className="text-xs text-slate-400">Pick before results land to compare your prediction.</div>
              </div>

              <div
                role="radiogroup"
                aria-label="Select your prediction"
                className={predictionLayout.container}
              >
                {modelStatuses.map((model, index) => (
                  <button
                    key={model.id}
                    ref={el => predictionRefs.current[index] = el}
                    role="radio"
                    aria-checked={selectedPrediction === model.id}
                    onClick={() => handlePredictionSelect(model.id, index)}
                    onKeyDown={(e) => handlePredictionKeyDown(e, index)}
                    className={`${predictionLayout.card} ${
                      selectedPrediction === model.id
                        ? 'border-cyan-400 bg-cyan-500/20 shadow-lg shadow-cyan-500/30 focus:ring-cyan-400'
                        : 'border-slate-600 bg-slate-800/50 hover:border-slate-500 focus:ring-slate-400'
                    }`}
                  >
                    <span className={predictionLayout.logoWrapper}>
                      <ModelLogo
                        modelId={model.id}
                        modelName={model.name}
                        size="lg"
                        className={predictionLayout.logo}
                      />
                    </span>
                    <div className={predictionLayout.title}>{model.name}</div>
                    {selectedPrediction === model.id && (
                      <div className={predictionLayout.selected}>✓ Selected</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      </main>

      {/* Step 7: Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="cancel-modal-title"
            aria-describedby="cancel-modal-desc"
          >
            <h3 id="cancel-modal-title" className="text-xl font-bold text-white mb-3">
              Cancel Processing?
            </h3>
            <p id="cancel-modal-desc" className="text-slate-300 mb-4">
              Are you sure you want to cancel this comparison? All progress will be lost and this comparison will not be saved.
            </p>
            <p className="text-red-300 text-sm mb-6">
              ⚠️ This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 bg-slate-600/50 text-slate-300 rounded-md hover:bg-slate-600/70 transition-colors focus:ring-2 focus:ring-slate-400 focus:outline-none"
              >
                Keep Processing
              </button>
              <button
                onClick={handleCancelConfirm}
                className="px-4 py-2 bg-red-500/20 text-red-300 border border-red-400/30 rounded-md hover:bg-red-500/30 transition-colors focus:ring-2 focus:ring-red-400 focus:outline-none"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 7: Edit & Resubmit Confirmation Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="edit-modal-title"
            aria-describedby="edit-modal-desc"
          >
            <h3 id="edit-modal-title" className="text-xl font-bold text-white mb-3">
              Edit & Resubmit Prompt?
            </h3>
            <p id="edit-modal-desc" className="text-slate-300 mb-4">
              This will stop the current processing and take you back to edit your prompt. The current comparison will not be saved.
            </p>
            <p className="text-yellow-300 text-sm mb-6">
              ⚠️ Your prompt and model selections will be pre-filled for editing.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-slate-600/50 text-slate-300 rounded-md hover:bg-slate-600/70 transition-colors focus:ring-2 focus:ring-slate-400 focus:outline-none"
              >
                Keep Processing
              </button>
              <button
                onClick={handleEditConfirm}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 rounded-md hover:bg-cyan-500/30 transition-colors focus:ring-2 focus:ring-cyan-400 focus:outline-none"
              >
                Yes, Edit Prompt
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </MainLayout>
  );
};

export default ProcessingPageNew;
